/**
 * Shared cart helpers that fire product_added_to_cart via /cart/add.js.
 * Main product lines have no Bags property, so Shopify merges on
 * variant + selling_plan. Quantity increases are add.js only after any
 * one-time legacy Bags strip via change.js (or remove+re-add when Bags
 * was the only property — Shopify ignores properties: {}).
 * Never wrap window.fetch from theme code — Shopify Web Pixels depend on it.
 */
(function (global) {
  // After a variant+plan is confirmed clean (or Bags stripped), skip cart.js
  // so repeat ATC stays a single /cart/add.js.
  const bagsCleanCache = {};

  function bagsCacheKey(variantId, sellingPlanId) {
    return String(variantId) + ':' + (sellingPlanId ? String(sellingPlanId) : '');
  }

  function cartAjaxUrl(routeUrl, fallback) {
    const base = routeUrl || fallback;
    if (!base) return fallback;
    if (base.slice(-3) === '.js') return base;
    return base + '.js';
  }

  function getCartJsonUrl() {
    return (global.routes && global.routes.cart_url ? global.routes.cart_url : '/cart') + '.js';
  }

  function postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    }).then(function (res) {
      if (!res.ok) throw new Error('Cart error ' + res.status);
      return res.json();
    });
  }

  function sellingPlanMatches(item, sellingPlanId) {
    const wanted = sellingPlanId ? String(sellingPlanId) : '';
    const allocation = item.selling_plan_allocation;
    const current =
      allocation && allocation.selling_plan && allocation.selling_plan.id
        ? String(allocation.selling_plan.id)
        : '';
    return current === wanted;
  }

  function getItemSellingPlanId(item) {
    const allocation = item && item.selling_plan_allocation;
    if (allocation && allocation.selling_plan && allocation.selling_plan.id) {
      return String(allocation.selling_plan.id);
    }
    return '';
  }

  function isGiftCartItem(item) {
    return Boolean(item && item.properties && item.properties._gift_item);
  }

  function lineHasBagsProperty(item) {
    return Boolean(item && item.properties && item.properties.Bags);
  }

  function clonePropertiesWithoutBags(properties) {
    const next = {};
    if (!properties) return next;
    const keys = Object.keys(properties);
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if (key === 'Bags') continue;
      next[key] = properties[key];
    }
    return next;
  }

  function findMatchingProteinLines(cart, variantId, sellingPlanId) {
    const matches = [];
    if (!cart || !cart.items) return matches;

    const wantedVariant = String(variantId);
    for (let i = 0; i < cart.items.length; i++) {
      const item = cart.items[i];
      if (isGiftCartItem(item)) continue;
      if (String(item.variant_id) !== wantedVariant) continue;
      if (!sellingPlanMatches(item, sellingPlanId)) continue;
      matches.push(item);
    }
    return matches;
  }

  /**
   * One-time cleanup: strip Bags (and collapse duplicate protein lines) so a
   * plain /cart/add.js merges into a single line. No-op when already clean.
   *
   * Note: Shopify ignores properties: {} on change.js, so when Bags was the
   * only property we remove the line and re-add it clean.
   */
  function stripBagsFromMatchingLines(matches) {
    if (!matches || matches.length === 0) {
      return Promise.resolve(null);
    }

    const hasBags = matches.some(function (item) {
      return lineHasBagsProperty(item);
    });
    const hasDuplicates = matches.length > 1;

    if (!hasBags && !hasDuplicates) {
      return Promise.resolve(null);
    }

    let totalQty = 0;
    for (let i = 0; i < matches.length; i++) {
      totalQty += matches[i].quantity;
    }

    const primary = matches[0];
    const cleanProps = clonePropertiesWithoutBags(primary.properties);
    const cleanPropKeys = Object.keys(cleanProps);
    const changeUrl = cartAjaxUrl(
      global.routes && global.routes.cart_change_url,
      '/cart/change.js'
    );
    const updateUrl = cartAjaxUrl(
      global.routes && global.routes.cart_update_url,
      '/cart/update.js'
    );
    const addUrl = cartAjaxUrl(
      global.routes && global.routes.cart_add_url,
      '/cart/add.js'
    );

    let chain = Promise.resolve(null);
    if (hasDuplicates) {
      const updates = {};
      for (let i = 1; i < matches.length; i++) {
        updates[matches[i].key] = 0;
      }
      chain = postJson(updateUrl, { updates: updates });
    }

    return chain.then(function () {
      if (cleanPropKeys.length > 0) {
        return postJson(changeUrl, {
          id: primary.key,
          quantity: totalQty,
          properties: cleanProps,
        });
      }

      if (!hasBags) {
        // Duplicates only, already same clean properties — bump primary qty.
        return postJson(changeUrl, {
          id: primary.key,
          quantity: totalQty,
        });
      }

      // Bags was the only property: remove + re-add clean so merge works.
      const sellingPlanId = getItemSellingPlanId(primary);
      return postJson(changeUrl, {
        id: primary.key,
        quantity: 0,
      }).then(function () {
        const addBody = {
          id: primary.variant_id,
          quantity: totalQty,
        };
        if (sellingPlanId) {
          addBody.selling_plan = parseInt(sellingPlanId, 10);
        }
        return postJson(addUrl, addBody);
      });
    });
  }

  function stripBagsForVariant(variantId, sellingPlanId) {
    if (!variantId) {
      return Promise.resolve(null);
    }

    const cacheKey = bagsCacheKey(variantId, sellingPlanId || '');
    if (bagsCleanCache[cacheKey]) {
      return Promise.resolve(null);
    }

    const drawer = document.querySelector('cart-drawer');
    // Empty cart: nothing to strip. Do not cache — Bags may be seeded later.
    if (drawer && drawer.classList.contains('is-empty')) {
      return Promise.resolve(null);
    }

    return fetch(getCartJsonUrl(), { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('Cart fetch error');
        return res.json();
      })
      .then(function (cart) {
        const matches = findMatchingProteinLines(
          cart,
          variantId,
          sellingPlanId || ''
        );
        return stripBagsFromMatchingLines(matches).then(function (result) {
          bagsCleanCache[cacheKey] = true;
          return result;
        });
      })
      .catch(function () {
        return null;
      });
  }

  function markBagsClean(variantId, sellingPlanId) {
    if (!variantId) return;
    bagsCleanCache[bagsCacheKey(variantId, sellingPlanId || '')] = true;
  }

  /**
   * Increase quantity with /cart/add.js (fires ATC pixel).
   * Strips legacy Bags first when needed so Shopify merges correctly.
   */
  function addDeltaThenReconcile(options) {
    const opts = options || {};
    const variantId = opts.variantId;
    const deltaQty = parseInt(opts.deltaQty, 10) || 1;
    const sellingPlanId = opts.sellingPlanId || '';
    const sections = opts.sections || null;

    if (!variantId || deltaQty < 1) {
      return Promise.reject(new Error('Invalid add-delta payload'));
    }

    const addBody = {
      id: variantId,
      quantity: deltaQty,
    };

    if (sellingPlanId) {
      addBody.selling_plan = parseInt(sellingPlanId, 10);
    }

    if (sections && sections.sections) {
      addBody.sections = sections.sections;
      addBody.sections_url = sections.sections_url;
    }

    return stripBagsForVariant(variantId, sellingPlanId).then(function () {
      return postJson(
        cartAjaxUrl(
          global.routes && global.routes.cart_add_url,
          '/cart/add.js'
        ),
        addBody
      );
    });
  }

  global.ForallCartTracking = {
    findMatchingProteinLines: findMatchingProteinLines,
    isGiftCartItem: isGiftCartItem,
    sellingPlanMatches: sellingPlanMatches,
    stripBagsFromMatchingLines: stripBagsFromMatchingLines,
    stripBagsForVariant: stripBagsForVariant,
    markBagsClean: markBagsClean,
    addDeltaThenReconcile: addDeltaThenReconcile,
    postJson: postJson,
    getCartJsonUrl: getCartJsonUrl,
  };
})(window);
